import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import { openFutbolModule, getFutbolModulePublicUrl } from '../../utils/futbolModule';

const SPORT_OPTIONS = [
  { value: 'football', label: 'Fútbol' },
  { value: 'volleyball', label: 'Voleibol' },
];

/**
 * Maestros academia.app (super_admin): clubs/escuelas + SSO al Nest.
 * Independiente de D28D / Food / Training / Fit Live.
 */
export default function FutbolAdminView({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({
    name: '', external_id: '', slug: '', sport: 'football', multiSedes: false, sedesText: '',
  });
  const emptyMember = {
    email: '', nombre: '', password: '', futbol_role: 'PLAYER', school_external_id: '',
  };
  const [member, setMember] = useState(emptyMember);
  const [editId, setEditId] = useState(null);
  const [edit, setEdit] = useState({
    name: '', slug: '', sport: 'football', active: true, external_id: '',
  });
  const [filterQ, setFilterQ] = useState('');
  const [filterSport, setFilterSport] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [selectedClub, setSelectedClub] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/futbol-module/schools');
      setSchools(data?.data?.schools || []);
      if (data?.success === false) setError(data?.data?.error || 'No se pudo listar clubs');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Error al listar clubs');
      setSchools([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createClub = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMsg('');
    try {
      const name = form.name.trim() || form.slug.trim().replace(/-/g, ' ');
      if (!name) {
        setError('Indica el nombre del club (o al menos el slug)');
        setLoading(false);
        return;
      }
      const externalId = form.external_id.trim()
        || `CLUB-${name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').slice(0, 40)}`;
      const campuses = form.multiSedes
        ? form.sedesText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean)
        : ['Sede Principal'];
      const { data } = await api.post('/futbol-module/schools', {
        name,
        external_id: externalId,
        slug: form.slug.trim() || undefined,
        sport: form.sport || 'football',
        campuses,
      });
      if (!data?.success) {
        setError(data?.data?.error || 'No se pudo crear el club');
      } else {
        const sportName = data.data?.school?.sportLabel
          || SPORT_OPTIONS.find((o) => o.value === (data.data?.school?.sport || form.sport))?.label
          || form.sport;
        const sedeNames = (data.data?.school?.campuses || []).map((c) => c.name).join(', ')
          || campuses.join(', ');
        setMsg(`Club creado: ${data.data?.school?.name || name} (${sportName}) · sedes: ${sedeNames}`);
        setForm({
          name: '', external_id: '', slug: '', sport: 'football', multiSedes: false, sedesText: '',
        });
        await load();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.data?.error || err.message || 'Error al crear club');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (s) => {
    const ext = s.externalId || s.external_id;
    setEditId(ext);
    setEdit({
      name: s.name || '',
      slug: s.slug || '',
      sport: s.sport || 'football',
      active: s.active !== false,
      external_id: ext,
    });
    setError('');
    setMsg('');
  };

  const saveEdit = async () => {
    if (!editId) return;
    setLoading(true);
    setError('');
    setMsg('');
    try {
      const { data } = await api.patch(`/futbol-module/schools/${encodeURIComponent(editId)}`, {
        name: edit.name.trim(),
        slug: edit.slug.trim(),
        sport: edit.sport,
        active: edit.active,
      });
      if (!data?.success) {
        setError(data?.data?.error || 'No se pudo actualizar el club');
      } else {
        setMsg(`Club actualizado: ${data.data?.school?.name || edit.name}`);
        setEditId(null);
        await load();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.data?.error || err.message || 'Error al editar');
    } finally {
      setLoading(false);
    }
  };

  const removeClub = async (s) => {
    const ext = s.externalId || s.external_id;
    const label = s.name || ext;
    const members = Number(s.members) || 0;
    const hard = members === 0
      && window.confirm(`¿Eliminar definitivamente «${label}»?\nSin miembros: se borra de la BD.`);
    if (members > 0) {
      if (!window.confirm(`«${label}» tiene ${members} miembro(s).\nSe desactivará (no se borra) para no perder accesos.`)) {
        return;
      }
    } else if (!hard) {
      if (!window.confirm(`¿Desactivar «${label}»?`)) return;
    }
    setLoading(true);
    setError('');
    setMsg('');
    try {
      const qs = hard ? '?hard=1' : '';
      const { data } = await api.delete(`/futbol-module/schools/${encodeURIComponent(ext)}${qs}`);
      if (!data?.success) {
        setError(data?.data?.error || 'No se pudo eliminar/desactivar el club');
      } else {
        setMsg(data.data?.hard || data.data?.deleted
          ? `Club eliminado: ${label}`
          : `Club desactivado: ${label}`);
        if (editId === ext) setEditId(null);
        await load();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.data?.error || err.message || 'Error al eliminar');
    } finally {
      setLoading(false);
    }
  };

  const grantMember = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMsg('');
    try {
      if (!member.school_external_id) {
        setError('Selecciona el club al que das acceso');
        setLoading(false);
        return;
      }
      if (member.password && member.password.length > 0 && member.password.length < 8) {
        setError('La contraseña debe tener al menos 8 caracteres (o déjala vacía si la cuenta ya existe)');
        setLoading(false);
        return;
      }
      const { data } = await api.post('/futbol-module/members', {
        email: member.email.trim(),
        nombre: member.nombre.trim(),
        password: member.password,
        futbol_role: member.futbol_role,
        school_external_id: member.school_external_id,
      });
      if (!data?.success) {
        setError(data?.data?.error || 'No se pudo dar acceso a academia.app');
      } else {
        const synced = data.data?.password_synced
          ? ' · clave sincronizada para login en academia.app'
          : '';
        setMsg(`${data.data?.created ? 'Cuenta creada' : 'Acceso agregado'}: ${data.data?.email} (${data.data?.futbol_role})${synced}`);
        setMember(emptyMember);
        await load();
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.data?.error || err.message || 'Error al dar acceso');
    } finally {
      setLoading(false);
    }
  };

  const openAdmin = async () => {
    setLoading(true);
    setError('');
    const out = await openFutbolModule('/dashboard', '/admin');
    if (!out?.ok) setError(out?.error || 'No se pudo abrir academia.app');
    setLoading(false);
  };

  return (
    <div className="dashboard-main-view surface-readable ui-light-panel d28d-admin-surface w-full max-w-none p-4 sm:p-6 rounded-xl border border-slate-200">
      <button type="button" className="btn-secondary text-sm mb-4" onClick={onBack}>
        ← Volver a Maestros
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h3 className="text-xl font-bold text-stone-900 mb-1">Maestros academia.app</h3>
          <p className="text-stone-600 text-sm max-w-3xl">
            Clubs / escuelas de
            {' '}
            <strong>academia.app</strong>
            {' '}
            (producto independiente; packs por deporte). No usa gym_id ni ciclos D28D.
            URL módulo:
            {' '}
            {getFutbolModulePublicUrl()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <button type="button" className="btn-primary" disabled={loading} onClick={openAdmin}>
            {loading ? 'Conectando…' : 'Abrir admin academia.app (SSO)'}
          </button>
          <button type="button" className="btn-secondary" disabled={loading} onClick={load}>
            Recargar clubs
          </button>
        </div>
      </div>

      {error ? <p className="text-sm text-red-600 mb-3">{error}</p> : null}
      {msg ? <p className="text-sm text-emerald-700 mb-3">{msg}</p> : null}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-6">
        <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h4 className="font-semibold text-stone-900 mb-2">Crear club / escuela</h4>
          <p className="text-xs text-stone-500 mb-3">
            Nombre obligatorio (si solo pones slug, se usa como nombre). Se guarda en la BD
            {' '}
            <code className="text-stone-700">futbolplan.schools</code>
            .
          </p>
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={createClub}>
            <label className="block text-sm text-stone-700 sm:col-span-2">
              Nombre del club
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={160}
                placeholder="Academia Norte"
              />
            </label>
            <label className="block text-sm text-stone-700">
              Deporte
              <select
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={form.sport}
                onChange={(e) => setForm((f) => ({ ...f, sport: e.target.value }))}
                required
              >
                {SPORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm text-stone-700">
              Slug (opcional)
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                maxLength={120}
                placeholder="academia-norte"
              />
            </label>
            <label className="block text-sm text-stone-700 sm:col-span-2">
              ID externo (opcional)
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={form.external_id}
                onChange={(e) => setForm((f) => ({ ...f, external_id: e.target.value }))}
                maxLength={80}
                placeholder="CLUB-NORTE-01"
              />
            </label>
            <fieldset className="sm:col-span-2 rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-sm font-medium text-stone-800">Sedes del club</legend>
              <label className="flex items-center gap-2 text-sm text-stone-700 mb-2">
                <input
                  type="checkbox"
                  checked={form.multiSedes}
                  onChange={(e) => setForm((f) => ({ ...f, multiSedes: e.target.checked }))}
                />
                Tiene varias sedes
              </label>
              {!form.multiSedes ? (
                <p className="text-xs text-stone-500">
                  Se guardará automáticamente
                  {' '}
                  <strong>Sede Principal</strong>
                  {' '}
                  (campus + venue) para programar en Calendario.
                </p>
              ) : (
                <label className="block text-sm text-stone-700">
                  Nombres de sedes (una por línea o separadas por coma)
                  <textarea
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                    rows={3}
                    value={form.sedesText}
                    onChange={(e) => setForm((f) => ({ ...f, sedesText: e.target.value }))}
                    placeholder={'Cancha Norte\nCancha Sur'}
                    required={form.multiSedes}
                  />
                </label>
              )}
            </fieldset>
            <div className="sm:col-span-2">
              <button type="submit" className="btn-primary" disabled={loading || (!form.name.trim() && !form.slug.trim())}>
                Crear club
              </button>
            </div>
          </form>
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h4 className="font-semibold text-stone-900 mb-1">Dar acceso a academia.app</h4>
          <p className="text-xs text-stone-500 mb-3">
            Correo nuevo: cuenta solo-academia con esta contraseña (también sirve para entrar en
            {' '}
            <strong>escuelasdeportivas…/access</strong>
            ). Correo existente: suma acceso; si envías contraseña ≥ 8, se actualiza el login del módulo.
          </p>
          <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={grantMember}>
            <label className="block text-sm text-stone-700">
              Correo
              <input
                type="email"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={member.email}
                onChange={(e) => setMember((m) => ({ ...m, email: e.target.value }))}
                required
              />
            </label>
            <label className="block text-sm text-stone-700">
              Nombre
              <input
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={member.nombre}
                onChange={(e) => setMember((m) => ({ ...m, nombre: e.target.value }))}
                required
                maxLength={120}
              />
            </label>
            <label className="block text-sm text-stone-700">
              Contraseña (login academia.app)
              <input
                type="password"
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={member.password}
                onChange={(e) => setMember((m) => ({ ...m, password: e.target.value }))}
                minLength={8}
                autoComplete="new-password"
                placeholder="Mín. 8 · cuentas nuevas"
              />
            </label>
            <label className="block text-sm text-stone-700">
              Rol en academia.app
              <select
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={member.futbol_role}
                onChange={(e) => setMember((m) => ({ ...m, futbol_role: e.target.value }))}
              >
                <option value="PLAYER">Jugador</option>
                <option value="COACH">Coach</option>
                <option value="SCHOOL_ADMIN">Admin escuela</option>
                <option value="SPONSOR">Patrocinador / acudiente</option>
              </select>
            </label>
            <label className="block text-sm text-stone-700 sm:col-span-2">
              Club
              <select
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                value={member.school_external_id}
                onChange={(e) => setMember((m) => ({ ...m, school_external_id: e.target.value }))}
                required
              >
                <option value="">Selecciona…</option>
                {schools.filter((s) => s.active !== false).map((s) => {
                  const ext = s.externalId || s.external_id;
                  const sport = s.sportLabel || s.sport || '';
                  return (
                    <option key={ext || s.id} value={ext}>
                      {s.name}
                      {sport ? ` · ${sport}` : ''}
                      {' '}
                      (
                      {ext}
                      )
                    </option>
                  );
                })}
              </select>
            </label>
            <div className="sm:col-span-2">
              <button
                type="submit"
                className="btn-primary"
                disabled={loading || !member.email.trim() || !member.school_external_id}
              >
                Dar acceso
              </button>
            </div>
          </form>
        </section>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h4 className="font-semibold text-stone-900 mb-3">
          Clubs (
          {schools.length}
          )
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <label className="block text-sm text-stone-700">
            Buscar
            <input
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              placeholder="Nombre, slug o ID externo"
            />
          </label>
          <label className="block text-sm text-stone-700">
            Deporte
            <select
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={filterSport}
              onChange={(e) => setFilterSport(e.target.value)}
            >
              <option value="">Todos</option>
              {SPORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-stone-700">
            Estado
            <select
              className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </label>
        </div>
        {schools.length === 0 ? (
          <p className="text-sm text-stone-500">Aún no hay escuelas en futbolplan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full w-full text-sm border border-slate-200">
              <thead className="bg-slate-50 text-left">
                <tr>
                  <th className="px-3 py-2">Nombre</th>
                  <th className="px-3 py-2">Deporte</th>
                  <th className="px-3 py-2">Sedes</th>
                  <th className="px-3 py-2">Plantilla</th>
                  <th className="px-3 py-2">Miembros</th>
                  <th className="px-3 py-2">Activo</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {schools.filter((s) => {
                  const q = filterQ.trim().toLowerCase();
                  const ext = String(s.externalId || s.external_id || '').toLowerCase();
                  const hay = `${s.name || ''} ${s.slug || ''} ${ext}`.toLowerCase();
                  if (q && !hay.includes(q)) return false;
                  if (filterSport && (s.sport || 'football') !== filterSport) return false;
                  if (filterActive === 'active' && s.active === false) return false;
                  if (filterActive === 'inactive' && s.active !== false) return false;
                  return true;
                }).map((s) => {
                  const ext = s.externalId || s.external_id;
                  const sedes = Array.isArray(s.campuses) ? s.campuses.map((c) => c.name).join(', ') : '';
                  return (
                    <tr
                      key={s.id || ext}
                      className={`border-t border-slate-100 align-top ${selectedClub?.id === s.id ? 'bg-emerald-50/60' : ''}`}
                    >
                      <td className="px-3 py-2 font-medium text-stone-900">
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => setSelectedClub(s)}
                        >
                          {s.name}
                        </button>
                        <div className="text-xs text-stone-500 font-normal">{ext}</div>
                      </td>
                      <td className="px-3 py-2 text-stone-600">{s.sportLabel || s.sport || 'Fútbol'}</td>
                      <td className="px-3 py-2 text-stone-600">
                        {s.campusesCount ?? (Array.isArray(s.campuses) ? s.campuses.length : 0)}
                        {sedes ? ` · ${sedes}` : ''}
                      </td>
                      <td className="px-3 py-2 text-stone-600">
                        {s.playersCount ?? 0}
                        {' '}
                        jug. /
                        {' '}
                        {s.coachesCount ?? 0}
                        {' '}
                        coach /
                        {' '}
                        {s.teamsCount ?? 0}
                        {' '}
                        eq.
                      </td>
                      <td className="px-3 py-2">{s.members ?? '—'}</td>
                      <td className="px-3 py-2">{s.active === false ? 'No' : 'Sí'}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            disabled={loading}
                            onClick={() => setSelectedClub(s)}
                          >
                            Ver
                          </button>
                          <button
                            type="button"
                            className="btn-secondary text-xs"
                            disabled={loading}
                            onClick={() => openEdit(s)}
                          >
                            Editar
                          </button>
                          <button
                            type="button"
                            className="btn-secondary text-xs text-red-700 border-red-200"
                            disabled={loading}
                            onClick={() => removeClub(s)}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedClub ? (
          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-4">
            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
              <h5 className="font-semibold text-stone-900">
                Ficha del club ·
                {' '}
                {selectedClub.name}
              </h5>
              <button type="button" className="btn-secondary text-xs" onClick={() => setSelectedClub(null)}>
                Cerrar ficha
              </button>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-stone-700">
              <div>
                <dt className="text-xs text-stone-500">External ID</dt>
                <dd className="font-medium">{selectedClub.externalId || selectedClub.external_id}</dd>
              </div>
              <div>
                <dt className="text-xs text-stone-500">Slug</dt>
                <dd className="font-medium">{selectedClub.slug || '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-stone-500">Deporte</dt>
                <dd className="font-medium">{selectedClub.sportLabel || selectedClub.sport || 'Fútbol'}</dd>
              </div>
              <div>
                <dt className="text-xs text-stone-500">Miembros / jugadores / coaches</dt>
                <dd className="font-medium">
                  {selectedClub.members ?? 0}
                  {' / '}
                  {selectedClub.playersCount ?? 0}
                  {' / '}
                  {selectedClub.coachesCount ?? 0}
                </dd>
              </div>
              <div>
                <dt className="text-xs text-stone-500">Equipos</dt>
                <dd className="font-medium">{selectedClub.teamsCount ?? 0}</dd>
              </div>
              <div>
                <dt className="text-xs text-stone-500">Activo</dt>
                <dd className="font-medium">{selectedClub.active === false ? 'No' : 'Sí'}</dd>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-xs text-stone-500">Sedes (campus)</dt>
                <dd className="font-medium">
                  {(Array.isArray(selectedClub.campuses) && selectedClub.campuses.length)
                    ? selectedClub.campuses.map((c) => c.name).join(' · ')
                    : 'Sin sedes registradas'}
                </dd>
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-xs text-stone-500">Venues (programación)</dt>
                <dd className="font-medium">
                  {(Array.isArray(selectedClub.venues) && selectedClub.venues.length)
                    ? selectedClub.venues.map((v) => v.name).join(' · ')
                    : 'Sin venues'}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-stone-500 mt-3">
              Funciones del club en academia.app: Plantilla, Personas, Calendario (entrenamientos/campeonato),
              Asistencia, Seguimiento (cancha + observaciones), Ranking, Ficha jugador, Metodología.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn-primary text-xs" disabled={loading} onClick={openAdmin}>
                Abrir admin academia.app
              </button>
              <button type="button" className="btn-secondary text-xs" onClick={() => openEdit(selectedClub)}>
                Editar club
              </button>
            </div>
          </div>
        ) : null}

        {editId ? (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <h5 className="font-semibold text-stone-900 mb-2">
              Editar club ·
              {' '}
              {editId}
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <label className="block text-sm text-stone-700">
                Nombre
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  value={edit.name}
                  onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))}
                />
              </label>
              <label className="block text-sm text-stone-700">
                Slug
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  value={edit.slug}
                  onChange={(e) => setEdit((x) => ({ ...x, slug: e.target.value }))}
                />
              </label>
              <label className="block text-sm text-stone-700">
                Deporte
                <select
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  value={edit.sport}
                  onChange={(e) => setEdit((x) => ({ ...x, sport: e.target.value }))}
                >
                  {SPORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm text-stone-700">
                Activo
                <select
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-2"
                  value={edit.active ? '1' : '0'}
                  onChange={(e) => setEdit((x) => ({ ...x, active: e.target.value === '1' }))}
                >
                  <option value="1">Sí</option>
                  <option value="0">No</option>
                </select>
              </label>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button type="button" className="btn-primary" disabled={loading} onClick={saveEdit}>
                Guardar cambios
              </button>
              <button type="button" className="btn-secondary" onClick={() => setEditId(null)}>
                Cancelar
              </button>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}
